import { toast } from "react-toastify"
import { Star } from "@phosphor-icons/react"

import { IconButton } from "~/components/design-system/components"
import { useRegisterSearchCommands } from "~/components/sidebar/search/useRegisterSearchCommands"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useCurrentRoute, useFavorites } from "~/hooks/useFavorites"
import { useResolveNavModule } from "~/hooks/useNavModule"
import { trackEvent } from "~/hooks/useTrackingService"
import { pageTitleFrom, useNavContextStore } from "~/stores/nav-context-store"

/** Star after the page breadcrumb that adds the page (not a specific view) to sidebar favorites. */
export const FavoritePageButton = () => {
  const route = useCurrentRoute({ page: true })
  const favorites = useFavorites()
  const resolveNavModule = useResolveNavModule()
  const breadcrumb = useNavContextStore((s) => s.breadcrumb)
  const tabLabel = useNavContextStore((s) => s.tabLabel)
  const entityLabel = useNavContextStore((s) => s.entityLabel)
  const entitySubtitle = useNavContextStore((s) => s.entitySubtitle)

  const title = pageTitleFrom({ breadcrumb, tabLabel, entityLabel })?.trim()
  const navModule = resolveNavModule(route)
  // The first descriptor that adds something to the title: the tab, else where it sits in the sidebar.
  const context = [tabLabel, navModule?.pageName, navModule?.name].find(
    (part) => part && part !== title,
  )
  const isFavorite = favorites.isFavorite(route)
  const enabled = !!title
  // Distinct from a view pill's "Add to favorites", which favorites that view.
  const label = isFavorite ? "Remove page from favorites" : "Add page to favorites"

  const toggle = (source: "Page Header" | "Command Center") => {
    if (!title) return
    const inFolder = favorites.isInFolder(route)
    const added = favorites.toggle({
      route,
      label: title,
      context: context ?? undefined,
      subtitle: entitySubtitle ?? undefined,
    })
    trackEvent(added ? "Sidebar Favorite Added" : "Sidebar Favorite Removed", {
      "Favorite Type": "Page",
      Module: navModule?.name,
      "In Folder": inFolder,
      Source: source,
    })
    toast.success(added ? `"${title}" added to favorites` : `"${title}" removed from favorites`)
  }

  useRegisterSearchCommands(
    enabled
      ? [
          {
            id: "toggle-page-favorite",
            name: label,
            keywords: ["favorite", "star", "bookmark"],
            icon: <Star weight={isFavorite ? "fill" : "regular"} />,
            run: () => toggle("Command Center"),
          },
        ]
      : [],
    [enabled, isFavorite],
  )

  if (!enabled) return null

  return (
    <Tooltip title={label}>
      <IconButton
        variant="transparent"
        size="xs"
        aria-label={label}
        onClick={() => toggle("Page Header")}
      >
        <Star weight={isFavorite ? "fill" : "regular"} />
      </IconButton>
    </Tooltip>
  )
}
